import { useLazyQuery } from '@apollo/client'
import { useEffect, useRef, useState } from 'react'
import { PREVIEW_DISCOUNT } from '../graphql/discount-queries'
import { useCart } from '../contexts/CartContext'

export interface DiscountPreview {
  discount_amount_cents: number
  discounted_total_cents: number
  product_discount_cents: number
  product_discount_name: string | null
  order_discount_cents: number
  order_discount_name: string | null
  // Tri-state: null = no code sent, true = code valid, false = code rejected.
  coupon_applied: boolean | null
  // Reason the coupon was rejected (only set when coupon_applied === false).
  coupon_message: string | null
  // Curry Me Club: the reward's value on this basket, already folded into
  // discount_amount_cents and discounted_total_cents (0 when none / not applicable).
  loyalty_reward_cents: number
  loyalty_reward_name_nl: string | null
  loyalty_reward_name_en: string | null
  // Why the reward did not apply; null when it did. Shown, but never blocks checkout.
  loyalty_message: string | null
}

interface UseDiscountPreviewResult {
  preview: DiscountPreview | null
  loading: boolean
  couponError: string | null
}

export function useDiscountPreview(
  couponCode: string,
  counterCode?: string
): UseDiscountPreviewResult {
  const { cart } = useCart()
  const [preview, setPreview] = useState<DiscountPreview | null>(null)
  const [couponError, setCouponError] = useState<string | null>(null)

  const [fetchPreview, { loading }] = useLazyQuery(PREVIEW_DISCOUNT, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const result: DiscountPreview = data.previewDiscount
      setPreview(result)
      // previewDiscount does not throw for a bad code — it reports the outcome
      // via coupon_applied. Surface the rejection reason when the code failed.
      setCouponError(
        result.coupon_applied === false
          ? result.coupon_message ?? 'Coupon could not be applied'
          : null
      )
    },
    onError: (error) => {
      setPreview(null)
      // Surface the first meaningful error message from the API
      const message = error.graphQLErrors?.[0]?.message ?? error.message
      setCouponError(message)
    },
  })

  // Debounce refs — separate timers for cart changes vs coupon code changes
  const cartDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const couponDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Build the query variables from the current cart state
  const buildVariables = (code: string) => ({
    lines: cart.items
      .filter((item) => item.type === 'main')
      .map((item) => {
        const optionsTotal = item.selected_options.reduce(
          (sum, opt) => sum + opt.price_delta_cents,
          0
        )
        return {
          product_variant_id: item.product_variant_id,
          quantity: item.quantity,
          total_cents: (item.price_cents + optionsTotal) * item.quantity,
        }
      }),
    total: cart.total_cents,
    code: code || undefined,
    type: cart.order_type.toLowerCase(), // API expects 'dine_in' / 'takeaway'
    // The counter code names the member AND the reward; the API prices the
    // reward into discounted_total_cents. Never combined with reward_id here.
    loyalty: counterCode ? { counter_code: counterCode } : undefined,
  })

  // Re-run when cart items or total change (300 ms debounce)
  useEffect(() => {
    if (cart.items.length === 0) {
      setPreview(null)
      setCouponError(null)
      return
    }

    if (cartDebounceRef.current) clearTimeout(cartDebounceRef.current)
    cartDebounceRef.current = setTimeout(() => {
      fetchPreview({ variables: buildVariables(couponCode) })
    }, 300)

    return () => {
      if (cartDebounceRef.current) clearTimeout(cartDebounceRef.current)
    }
  }, [cart.items, cart.total_cents, cart.order_type])

  // Re-run when the coupon OR the club code changes (600 ms debounce). The club
  // code is applied on a button press, not per keystroke, so this re-prices the
  // basket the moment a code is applied or removed.
  useEffect(() => {
    if (cart.items.length === 0) return

    if (couponDebounceRef.current) clearTimeout(couponDebounceRef.current)
    couponDebounceRef.current = setTimeout(() => {
      fetchPreview({ variables: buildVariables(couponCode) })
    }, 600)

    return () => {
      if (couponDebounceRef.current) clearTimeout(couponDebounceRef.current)
    }
  }, [couponCode, counterCode])

  return { preview, loading, couponError }
}
